<script setup lang="ts">
import { computed, ref, useCssModule } from "vue";

import type { I_WIDGET_STEPS } from "@/stores/tiw/tiw.types";

import Modal from "@/components/modal/modal.vue";
import NewDeviceCard from "@/components/NewDeviceCard/NewDeviceCard.vue";
import StepsButtons from "@/components/StepsButtons/StepsButtons.vue";
import globalClasses from "@/css/global-classes.module.css";
import { usePostQuestDevice } from "@/hooks/usePostQuestDevice/usePostQuestDevice";
import useTranslate from "@/hooks/useTranslate/useTranslate";
import { useChooseDeviceStore } from "@/stores/chooseDevice/chooseDevice";
import { useHowYouRateDeviceStore } from "@/stores/howYouRateDevice/howYouRateDevice";
import { useTIWStore } from "@/stores/tiw/tiw";
import Button from "@/ui/button/button.vue";

const classes = useCssModule();

const TIWStore = useTIWStore();
const chooseDeviceStore = useChooseDeviceStore();
const howYouRateDeviceStore = useHowYouRateDeviceStore();

const DEVICES = [
    {
        title: "Samsung S25 128Gb",
        imageUrl: `${import.meta.env.VITE_APP_URL}/img/abstract-device.svg`,
        chain: ["Samsung", "S25", "128Gb"],
        id: "SM-S938BZBBSEK",
    },
    {
        title: "Samsung S25 256Gb",
        imageUrl: `${import.meta.env.VITE_APP_URL}/img/abstract-device.svg`,
        chain: ["Samsung", "S25", "256Gb"],
        id: "SM-S931BDBBSEK",
    },
    {
        title: useTranslate("tiw__chooseDeviceToBuy_otherDevice"),
        imageUrl: `${import.meta.env.VITE_APP_URL}/img/abstract-device.svg`,
        id: "",
    },
];

function selectDevice(id: string) {
    TIWStore.INJECTED_GOOD.article = id;
}

const isLoadingToNext = ref(false);
const isNextButtonDisabled = computed(
    () =>
        isLoadingToNext.value ||
        chooseDeviceStore.isDeviceNotAccepted ||
        (!TIWStore.INJECTED_GOOD.article && TIWStore.INJECTED_GOOD.article !== ""),
);

async function clickToNext() {
    isLoadingToNext.value = true;
    await usePostQuestDevice();
    // TODO: полумай, что делать, если в запросе строкой выше выпадает ошибка.
    isLoadingToNext.value = false;

    try {
        const selectedDeviceData = DEVICES.find(
            (device) => device.id === TIWStore.INJECTED_GOOD.article,
        );
        howYouRateDeviceStore.newDeviceToBuyData = {
            chain: selectedDeviceData.chain,
            imgSrc: selectedDeviceData.imageUrl,
        };
    } catch (e) {
        console.error("Error when add new device data to store: ", e);
    }

    const nextStep: I_WIDGET_STEPS = chooseDeviceStore.isDeviceNotAccepted
        ? "chooseDevice"
        : "howYouRateDevice";
    TIWStore.switchStep(nextStep);
}

function skipStep() {
    selectDevice("");
    clickToNext();
}
</script>

<template>
    <Modal :class="classes.modal">
        <template #header>
            <p class="tiw__h2" :class="classes.header">
                {{ useTranslate("tiw__chooseDeviceToBuy_title") }}
            </p>
            <!-- FIXME перевод -->
            <p>Sprawdź, czy przysługuje Ci dodatkowy rabat na wybrany model.</p>
        </template>
        <div :class="classes.newModelsContainer">
            <div :class="classes.newModels">
                <NewDeviceCard
                    v-for="device of DEVICES"
                    :key="device.id"
                    :title="device.title"
                    :image-url="device.imageUrl"
                    :is-selected="device.id === TIWStore.INJECTED_GOOD.article"
                    :id-to-select="device.id"
                    @select="selectDevice"
                />
            </div>
        </div>

        <StepsButtons
            :click-to-next="clickToNext"
            :loading="isLoadingToNext"
            :disabled="isNextButtonDisabled"
        >
            <template #additionalBtn>
                <!-- TODO перевод -->
                <Button
                    type="button"
                    :class="globalClasses.additionalButton"
                    :loading="isLoadingToNext"
                    @click="skipStep"
                    >Pomiń ten krok</Button
                >
            </template>
        </StepsButtons>
    </Modal>
</template>

<style module>
.modal {
    --gap: 24px;
}
.header {
    text-wrap: pretty;
}
.newModelsContainer {
    container-type: inline-size;
    container-name: modal-container;
}
.newModels {
    display: flex;
    flex-direction: column;
    gap: var(--gap);
}
@container (min-width: 448px) {
    .newModels {
        display: grid;
        grid-template-columns: 1fr 1fr;
    }
}
</style>
